import { useMemo } from "react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { UIFlightData } from "@flights/types";
import flights_web_fd_fare_rules_source, {
  useTrackFareRulesSource
} from "../../../../../../../utils/experiments/funnel/flights_web_fd_fare_rules_source";
import flights_web_fd_rename_fare_rules from "../../../../../../../utils/experiments/funnel/flights_web_fd_rename_fare_rules";
import useProfiler from "../../../../../../../hooks/useProfiler";
import { useSelector } from "react-redux";
import { getFlightDetailsFetchStatus } from "../../../../../../../store/flightDetails/selectors";
import { grumble } from "../../../../../../../utils/flog";
import usePointOfSale from "../../../../../../../hooks/usePointOfSale";

type FareRulesInfoData = { free: boolean; label: string } | undefined;
export type FareRulesInfo = Record<"changeable" | "cancellable", FareRulesInfoData>;

const useFareRulesInfoController = (flight: UIFlightData) => {
  const i18n = useI18n();
  const profiler = useProfiler();

  const isRenameFareRulesExp = flights_web_fd_rename_fare_rules.getExp(profiler.isMobile()).trackWithDefaultStage();

  const dataSource = useMemo(() => {
    const features = flight?.brandedFareInfo?.features || [];
    const sellableFeatures = flight?.brandedFareInfo?.sellableFeatures || [];
    const allFeatures = [...features, ...sellableFeatures];

    if (!allFeatures.length) return;

    return allFeatures;
  }, [flight?.brandedFareInfo?.features, flight?.brandedFareInfo?.sellableFeatures]);

  const trackFareRulesSource = useTrackFareRulesSource();

  const newDataSource =
    !Array.isArray(flight.fareRules) && flight.fareRules?.featuresSummary && !!flight.fareRules.featuresSummary.length
      ? flight.fareRules.featuresSummary
      : undefined;

  const { changeable, cancellable } = useMemo(() => {
    const result: FareRulesInfo = {
      changeable: undefined,
      cancellable: undefined
    };

    if (trackFareRulesSource()) {
      if (!newDataSource) {
        return result;
      }

      newDataSource.forEach((fr) => {
        if (fr.featureName === "CHANGEABLE_TICKET") {
          if (fr.availability === "INCLUDED") {
            result.changeable = {
              free: true,
              label: isRenameFareRulesExp
                ? i18n.trans(t("flights_fd_fare_change_no_fee"))
                : i18n.trans(t("flights_fd_fare_policy_change_no_fee"))
            } as FareRulesInfoData;
          }

          if (fr.availability === "SELLABLE") {
            result.changeable = {
              free: false,
              label: isRenameFareRulesExp
                ? i18n.trans(t("flights_fd_fare_change_fee"))
                : i18n.trans(t("flights_fd_fare_policy_change_fee"))
            } as FareRulesInfoData;
          }
        }

        if (fr.featureName === "CANCELLABLE_TICKET") {
          if (fr.availability === "INCLUDED") {
            result.cancellable = {
              free: true,
              label: isRenameFareRulesExp
                ? i18n.trans(t("flights_fd_fare_cancel_no_fee"))
                : i18n.trans(t("flights_fd_fare_policy_cancel_no_fee"))
            } as FareRulesInfoData;
          }

          if (fr.availability === "SELLABLE") {
            result.cancellable = {
              free: false,
              label: isRenameFareRulesExp
                ? i18n.trans(t("flights_fd_fare_cancel_fee"))
                : i18n.trans(t("flights_fd_fare_policy_cancel_fee"))
            } as FareRulesInfoData;
          }
        }
      });
    } else {
      if (!dataSource?.length) {
        return result;
      }

      dataSource.forEach((feature) => {
        switch (feature.featureName) {
          case "CHANGEABLE_TICKET":
            if (feature.availability === "INCLUDED") {
              result.changeable = {
                free: true,
                label: isRenameFareRulesExp
                  ? i18n.trans(t("flights_fd_fare_change_no_fee"))
                  : i18n.trans(t("flights_fd_fare_policy_change_no_fee"))
              };
            } else if (feature.availability === "SELLABLE") {
              result.changeable = {
                free: false,
                label: isRenameFareRulesExp
                  ? i18n.trans(t("flights_fd_fare_change_fee"))
                  : i18n.trans(t("flights_fd_fare_policy_change_fee"))
              };
            }
            break;
          case "REFUNDABLE_TICKET":
            if (feature.availability === "INCLUDED") {
              result.cancellable = {
                free: true,
                label: isRenameFareRulesExp
                  ? i18n.trans(t("flights_fd_fare_cancel_no_fee"))
                  : i18n.trans(t("flights_fd_fare_policy_cancel_no_fee"))
              };
            } else if (feature.availability === "SELLABLE") {
              result.cancellable = {
                free: false,
                label: isRenameFareRulesExp
                  ? i18n.trans(t("flights_fd_fare_cancel_fee"))
                  : i18n.trans(t("flights_fd_fare_policy_cancel_fee"))
              };
            }
            break;
          default:
            break;
        }
      });
    }

    return result;
  }, [dataSource, i18n, isRenameFareRulesExp, newDataSource, trackFareRulesSource]);

  /* start - flights_web_fd_fare_rules_source */
  // We need to wait for FD to be fully loaded otherwise data will be tracked differently.
  // "dataSource" is only available on FD and "newDataSouce" is available on FD and SR.
  const fetchStatus = useSelector(getFlightDetailsFetchStatus);
  const pos = usePointOfSale();

  if (fetchStatus === "success") {
    if (changeable) {
      if (changeable.free) {
        flights_web_fd_fare_rules_source.goals.change_included();
      } else {
        flights_web_fd_fare_rules_source.goals.change_sellable();
      }
    }

    if (cancellable) {
      if (cancellable.free) {
        flights_web_fd_fare_rules_source.goals.cancel_included();
      } else {
        flights_web_fd_fare_rules_source.goals.cancel_sellable();
      }
    }

    if (!changeable && !cancellable) {
      flights_web_fd_fare_rules_source.goals.no_fare_rules();
    }

    // Sends grumble to check inconsistency between fare rules source
    const dataSourceMap = Object.fromEntries(
      (dataSource ?? [])
        .filter(({ featureName }) => featureName === "CHANGEABLE_TICKET" || featureName === "REFUNDABLE_TICKET")
        .map(({ featureName, availability }) => [
          featureName === "REFUNDABLE_TICKET" ? "CANCELLABLE_TICKET" : featureName,
          availability
        ])
    ) as Record<string, string>;

    const newDataSourceMap = Object.fromEntries(
      (newDataSource ?? []).map(({ featureName, availability }) => [featureName, availability])
    ) as Record<string, string>;

    if (
      Object.keys(dataSourceMap).length !== Object.keys(newDataSourceMap).length ||
      !Object.entries(dataSourceMap).every(([k, v]) => newDataSourceMap[k] === v)
    ) {
      grumble("fareRules data source mismatch", {
        dataSourceMap,
        newDataSourceMap,
        pos,
        url: window.location.href
      });
    }
  }
  /* start - flights_web_fd_fare_rules_source */

  const hasFareRules = Boolean(cancellable || changeable);

  if (hasFareRules) {
    flights_web_fd_rename_fare_rules.trackStages(
      profiler,
      { changeable, cancellable },
      Boolean(flight.oneWayCombination)
    );
  }
  return {
    hasFareRules,
    header: i18n.trans(t("flights_fd_fare_rules_header")),
    subheader: isRenameFareRulesExp
      ? i18n.trans(t("flights_fd_fare_rules_subtitle_included_airline"))
      : i18n.trans(t("flights_fd_fare_rules_subhead_policy_info")),
    changeable,
    cancellable
  };
};

export default useFareRulesInfoController;
